import React from "react";

import {
  List,
  useTable,
  CreateButton,
  getDefaultSortOrder,
  TextField,
  FilterDropdown,
  useSelect,
} from "@refinedev/antd";
import { CopyableEmailTag } from "components/fields/CopyableEmailTag";

import {
  Table,
  Typography,
  Divider,
  Space,
  Tag,
  Select,
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
} from "antd";

import { IVesselAccount, IVessel } from "interfaces";
import { useApiUrl, useCustomMutation } from "@refinedev/core";
import axios from "axios";
import { colors } from "config/theme";
import { TOKEN_KEY } from "authProvider";

// The vessel-accounts API is now guarded (AuthGuard + admin role). These custom
// calls bypass the refine dataProvider's authed axios instance, so attach the
// bearer token explicitly or they 401.
const getAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
  },
});

const VesselAccounts: React.FC = () => {
    const [showPassword, setShowPassword] = React.useState<number | null>(null);
    const [isCreating, setIsCreating] = React.useState(false);
    const [isGeneratingPassword, setIsGeneratingPassword] = React.useState(false);
    const [form] = Form.useForm();
    const apiUrl = useApiUrl();
    
    const { tableProps, sorters: sorter, tableQuery: tableQueryResult } = useTable<IVesselAccount>({
      resource: "vessel-accounts",

      sorters: {
        initial: [
            {
                field: "id",
                order: "desc",
            },
        ]
      }
    });
    const { isLoading } = tableQueryResult;

    const { selectProps: vesselSelectProps } = useSelect<IVessel>({
      resource: "vessel",
      optionLabel: "name",
      optionValue: "imoNumber",

      onSearch: (value) => [
          {
              field: "name",
              operator: "contains",
              value,
          },
      ],

      pagination: {
        mode: "server",
        pageSize: 100
      }
    });

    const handleVesselSelect = async (value: string) => {
        if (value) {
            form.setFieldsValue({
                username: value.toUpperCase(),
            });
            
            try {
                setIsGeneratingPassword(true);
                const response = await axios.get(`${apiUrl}/vessel-accounts/generate-password`, getAuthHeaders());
                form.setFieldsValue({
                  password: response?.data?.pwd
                });
            } catch (error) {
                message.error("Failed to generate password");
            } finally {
                setIsGeneratingPassword(false);
            }
        }
    };

    const handleCreateAccount = async () => {
        try {
            setIsCreating(true);
            const values = await form.validateFields();
            await axios.post(`${apiUrl}/vessel-accounts/create-account`, values, getAuthHeaders());
            message.success("Vessel account created successfully");
            form.resetFields();
            tableQueryResult.refetch();
        } catch (error) {
            message.error("Failed to create vessel account. Please contact support.");
        } finally {
            setIsCreating(false);
        }
    };

    const { selectProps: nameSelectProps } = useSelect<IVesselAccount>({
      resource: "vessel-accounts",
      optionLabel: "name",
      optionValue: "name",

      pagination: {
        mode: "server",
        pageSize: 100
      }
    });

    const { selectProps: imoSelectProps } = useSelect<IVesselAccount>({
      resource: "vessel-accounts",
      optionLabel: "imoNumber",
      optionValue: "imoNumber",

      pagination: {
        mode: "server",
        pageSize: 100
      }
    });

  return (
    <List
      breadcrumb={false}
      headerProps={{
      // onBack: () => window.history.back(),
      subTitle: "active vessel accounts",
      tags: isLoading ? (   
        <Tag color="orange">loading</Tag>
      ) : (
        <Tag color="green">live</Tag>
      ),
      extra: (
        <React.Fragment>
          <Space>
            <CreateButton hidden/>
          </Space>
        </React.Fragment>
      ),
      ghost: true,
      footer: (
        <Divider plain>
          © {new Date().getFullYear()} e-Mariner NavSolutions
        </Divider>
      ),
    }}
  >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <div style={{
            backgroundColor: colors.containerBg,
            padding: '24px',
            borderRadius: '8px',
            width: '100%',
            boxSizing: 'border-box',
            border: `1.5px solid ${colors.containerBorder}`
          }}>
            <Form
              form={form}
              layout="inline"
              style={{ 
                width: '100%', 
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px'
              }}
            >
              <Form.Item
                name="vesselName"
                style={{ flex: 1 }}
              >
                <Select
                  placeholder="Select Vessel"
                  options={vesselSelectProps.options}
                  onChange={handleVesselSelect}
                  loading={vesselSelectProps.loading}
                  showSearch
                  filterOption={false}
                  onSearch={vesselSelectProps.onSearch}
                />
              </Form.Item>
              <Form.Item
                name="username"
                style={{ flex: 1 }}
              >
                <Input disabled placeholder="Username" />
              </Form.Item>
              <Form.Item
                name="password"
                style={{ flex: 1 }}
              >
                <Input 
                  disabled 
                  placeholder="Password" 
                  suffix={isGeneratingPassword ? <span className="anticon anticon-loading anticon-spin" /> : null}
                />
              </Form.Item>
              <Form.Item style={{ flex: 0.5 }}>
                <Button type="primary" onClick={handleCreateAccount} loading={isCreating}>
                  Create Account
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
      <Table {...tableProps} rowKey="id">
        <Table.Column
          dataIndex="name"
          key="name"
          title="Account Name"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("name", sorter)}
          sorter
          filterDropdown={(props) => (
            <FilterDropdown {...props}>
              <Select
                style={{ minWidth: 200 }}
                placeholder="Select Account Name"
                {...nameSelectProps}
              />
            </FilterDropdown>
          )}
        />
        <Table.Column
          dataIndex="username"
          key="username"
          title="Username"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("username", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="password"
          key="password"
          title="Password"
          render={(value, record: IVesselAccount) => {
            return (
              <Space>
                {showPassword === record.id ? (
                  <TextField value={value} />
                ) : (
                  <Tag color={`${colors.primary}25`} onClick={() => setShowPassword(showPassword === record.id ? null : record.id)} style={{ cursor: 'pointer', color: colors.primary, borderColor: colors.primary }}>
                    Display Password
                  </Tag>
                )}
              </Space>
            );
          }}
          defaultSortOrder={getDefaultSortOrder("password", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="email"
          key="email"
          title="Email"
          render={(value) => <CopyableEmailTag value={value} />}
          defaultSortOrder={getDefaultSortOrder("email", sorter)}
          sorter
        />
        <Table.Column
          dataIndex="imoNumber"
          key="imoNumber"
          title="IMO Number"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("imoNumber", sorter)}
          sorter
          filterDropdown={(props) => (
            <FilterDropdown {...props}>
              <Select
                style={{ minWidth: 200 }}
                placeholder="Select IMO"
                {...imoSelectProps}
              />
            </FilterDropdown>
          )}
        />
        <Table.Column
          dataIndex="vesselName"
          key="vesselName"
          title="Vessel Name"
          render={(value) => <TextField value={value} />}
          defaultSortOrder={getDefaultSortOrder("vesselName", sorter)}
          sorter
        />
      </Table>
    </List>
  );


};

export default VesselAccounts;
